import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { Grid, Input, Message, Divider, Image } from "semantic-ui-react";
import {
  convertAddressStreet,
  isPartyCoAgentUser,
  isPartyTcUser,
  isPartyAssistantUser,
  searchFilter,
} from "../../../app/common/util/util";
import { useMediaQuery } from "react-responsive";
import PartyDocViewList from "./PartyDocViewList";
import PartyDocSignList from "./PartyDocSignList";
import _ from "lodash";

// Co-agents, TCs, and assistants share the same party portal link as the
// agent they're attached to, so the logged-in person must be identified by
// email (via isPartyCoAgentUser/isPartyTcUser/isPartyAssistantUser) to know
// whether to label them as e.g. "Listing Agent", "Listing CoAgent",
// "Transaction Coordinator", or "Assistant to the Listing Agent".
function getPartyRoleLabel(party, transaction, isCoAgentUser, isTcUser, isAssistantUser) {
  const role = party?.role;
  if (!role) return "";

  if (role === "Buyer" || role === "Buyer 2" || role === "Buyer 3") return "Buyer";
  if (role === "Seller" || role === "Seller 2" || role === "Seller 3") return "Seller";
  if (role === "Other" || role === "Other 2" || role === "Other 3") return "Other";
  if (role === "TC (Mine)") return "Transaction Coordinator";
  if (role === "CoAgent (Mine)") {
    return transaction?.agentRepresents === "Buyer" ? "Buyer CoAgent" : "Listing CoAgent";
  }
  if (role === "Listing Agent" || role === "Listing Agent 2") {
    if (isTcUser) return "Transaction Coordinator for Lising Side";
    if (isAssistantUser) return "Assistant to the Listing Agent";
    return isCoAgentUser ? "Listing CoAgent" : "Listing Agent";
  }
  if (role === "Buyer Agent" || role === "Buyer Agent 2") {
    if (isTcUser) return "Transaction Coordinator for Buyer Side";
    if (isAssistantUser) return "Assistant to the Buyer Agent";
    return isCoAgentUser ? "Buyer CoAgent" : "Buyer Agent";
  }
  return role;
}

export default function PartyOverview() {
  const { docsForViewing, docsForSigning, party, docsForParty } = useSelector(
    (state) => state.party
  );
  const { transaction, allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { currentUser } = useSelector((state) => state.auth);
  const [searchTerms, setSearchTerms] = useState("");
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const { loading } = useSelector((state) => state.async);

  const loggedInEmail = currentUser?.email || currentUserProfile?.email;
  const isCoAgentUser = isPartyCoAgentUser(party, loggedInEmail);
  const isTcUser = isPartyTcUser(party, loggedInEmail);
  const isAssistantUser = isPartyAssistantUser(party, loggedInEmail);
  const partyRoleLabel = getPartyRoleLabel(party, transaction, isCoAgentUser, isTcUser, isAssistantUser);

  // Extract values for dependency array
  const docsForPartyDocs = docsForParty?.docs;
  const docsForViewingDocs = docsForViewing?.docs;
  const partyEmail = party?.email;

  // Filter documents for viewing - include docs where:
  // 1. The doc is in docsForViewing (not in signingRequestedFor for this email), OR
  // 2. Any client (primary, secondary, tertiary) sharing this email has signed the document
  const filteredDocsForViewing = useMemo(() => {
    if (!docsForPartyDocs || !partyEmail) return docsForViewingDocs || [];

    // Find all parties that share the same email as the logged-in party
    const partiesSharingEmail = allParties?.filter(p => p.email === partyEmail) || [];

    return docsForPartyDocs.filter(doc => {
      // If doc is already in docsForViewing (not in signingRequestedFor), include it
      if (!doc.signingRequestedFor || !doc.signingRequestedFor.includes(partyEmail)) {
        return true;
      }

      // Check if any party sharing this email has signed the document
      if (doc.signingDetails && partiesSharingEmail.length > 0) {
        for (const sharedParty of partiesSharingEmail) {
          const signingDetail = Object.values(doc.signingDetails).find(
            detail => detail.role === sharedParty.role && detail.signed === true
          );
          if (signingDetail) {
            // Also verify the email is still in sharingWith
            if (doc.sharingWith?.includes(partyEmail)) {
              return true;
            }
          }
        }
      }

      return false;
    });
  }, [docsForPartyDocs, docsForViewingDocs, partyEmail, allParties]);

  const docsViewing = searchFilter(filteredDocsForViewing, searchTerms);

  // Get documents for signing organized by role hierarchy:
  // - Primary client (orange section)
  // - Secondary client (blue section)
  // - Tertiary client (purple section)
  const { primaryClientDocs, secondaryClientDocs, tertiaryClientDocs } = useMemo(() => {
    const result = {
      primaryClientDocs: null,
      secondaryClientDocs: null,
      tertiaryClientDocs: null,
    };

    // Return empty if party data not loaded yet
    if (!party || !party.email) {
      return result;
    }

    // Helper function to get docs for a specific party role
    const getDocsForParty = (partyObj) => {
      if (!partyObj) return [];
      return docsForParty?.docs?.filter((doc) => {
        if (!doc.signingDetails) return false;
        if (!doc.sharingWithRole?.[_.camelCase(partyObj.role)]) return false;
        const signingDetail = Object.values(doc.signingDetails).find(
          (detail) => detail.role === partyObj.role && detail.sentOut === true
        );
        return signingDetail && signingDetail.signed !== true;
      }) || [];
    };

    // Determine the base role (Buyer or Seller)
    const baseRole = party.role.startsWith("Buyer") ? "Buyer" : "Seller";
    const primaryRole = baseRole;
    const secondaryRole = `${baseRole} 2`;
    const tertiaryRole = `${baseRole} 3`;

    // Find all relevant parties
    const primaryParty = allParties?.find((p) => p.role === primaryRole);
    const secondaryParty = allParties?.find((p) => p.role === secondaryRole);
    const tertiaryParty = allParties?.find((p) => p.role === tertiaryRole);

    // Check if secondary/tertiary share email with primary
    const secondarySharesEmail = secondaryParty && secondaryParty.email === party.email;
    const tertiarySharesEmail = tertiaryParty && tertiaryParty.email === party.email;

    // Get primary client docs (always show in orange)
    if (primaryParty && primaryParty.email === party.email) {
      const docs = getDocsForParty(primaryParty);
      if (docs.length > 0) {
        result.primaryClientDocs = {
          client: primaryParty,
          docs: docs,
        };
      }
    }

    // Get secondary client docs (show in blue if shares email)
    if (secondarySharesEmail) {
      const docs = getDocsForParty(secondaryParty);
      if (docs.length > 0) {
        result.secondaryClientDocs = {
          client: secondaryParty,
          docs: docs,
        };
      }
    }

    // Get tertiary client docs (show in purple if shares email)
    if (tertiarySharesEmail) {
      const docs = getDocsForParty(tertiaryParty);
      if (docs.length > 0) {
        result.tertiaryClientDocs = {
          client: tertiaryParty,
          docs: docs,
        };
      }
    }

    return result;
  }, [allParties, party, docsForParty]);

  if (!party || !party.email || loading) {
    return <LoadingComponent content="Loading documents..." />;
  }

  return (
    <>
      <Grid
        stackable
        className={
          isMobile
            ? "small padding"
            : "big vertical padding huge horizontal padding"
        }
      >
        <Grid.Row columns="equal" className="zero bottom padding">
          <Grid.Column className="zero bottom padding">
            <h2>
              {party && party.firstName
                ? "Welcome " +
                  (party.hasPoa && party.poaFullName
                    ? party.poaFullName.split(" ")[0]
                    : party.firstName) +
                  "!"
                : ""}
            </h2>
          </Grid.Column>
          <Grid.Column className="zero bottom padding">
            <div className={isMobile ? "float-left" : "text-center"}>
              <h2>
                {transaction?.address &&
                  convertAddressStreet(transaction?.address)}
              </h2>
            </div>
          </Grid.Column>
          <Grid.Column className="zero bottom padding">
            <div className={isMobile ? "float-left" : "float-right"}>
              {(transaction?.agentRepresents === "Seller" &&
                (party?.role === "Buyer" ||
                  party?.role === "Buyer 2" ||
                  party?.role === "Buyer 3")) ||
              (transaction?.agentRepresents === "Buyer" &&
                (party?.role === "Seller" ||
                  party?.role === "Seller 2" ||
                  party?.role === "Seller 3")) ? (
                <h4>Shared with you by your agent.</h4>
              ) : (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "15px" }}
                >
                  {transaction?.agentProfile?.profilePhotoUrl && (
                    <Image
                      src={transaction.agentProfile.profilePhotoUrl}
                      circular
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <h4 style={{ margin: 0 }}>
                    {(transaction?.agentName ||
                      transaction?.agentProfile?.firstName) && (
                      <>
                        Shared with you by: <br />[
                      </>
                    )}
                    {transaction?.agentRepresents === "Seller"
                      ? "Listing"
                      : transaction?.agentRepresents}{" "}
                    Agent] <br />
                    {transaction?.agentName ||
                      transaction?.agentProfile?.firstName +
                        " " +
                        transaction?.agentProfile?.lastName}
                  </h4>
                </div>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
        {partyRoleLabel && (
          <Grid.Row columns={1} className="zero vertical padding">
            <Grid.Column textAlign="center">
              <h4 style={{ margin: 0 }}>
                {party?.hasPoa
                  ? `You are the POA for the ${partyRoleLabel} ${party.firstName} ${party.lastName}`
                  : `You are the ${partyRoleLabel}`}
              </h4>
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Column width={16} className="zero vertical padding mobile hidden">
          <Divider />
        </Grid.Column>

              {/* no documents to sign */
              //console.log("docsForSigning", docsForSigning) 
              }
        { (!primaryClientDocs || primaryClientDocs.docs?.length === 0) &&
          (!secondaryClientDocs || secondaryClientDocs.docs?.length === 0) &&
          (!tertiaryClientDocs || tertiaryClientDocs.docs?.length === 0) &&
          (!docsForSigning || docsForSigning.docs?.length === 0) 
        
         && (
          <Grid.Column computer={16} className="large bottom margin">
            <Message
              fluid="true"
              style={{ paddingTop: "26px", paddingBottom: "30px" }}
            >
              <Message.Header style={{ fontSize: "18px" }}>
                Status: There are currently no documents to sign.
              </Message.Header>
            </Message>
          </Grid.Column>
        ) 
        }

        {/* Primary Client Documents - Orange Section */}
        {primaryClientDocs && primaryClientDocs.docs.length > 0 && (
          <Grid.Column computer={16} className="mini bottom margin">
            <Message
              fluid="true"
              color="orange"
              style={{ paddingTop: "26px", paddingBottom: "30px" }}
            >
              <Message.Header style={{ fontSize: "18px" }}>
                Documents to sign for {primaryClientDocs.client.firstName}{" "}
                {primaryClientDocs.client.lastName} ({primaryClientDocs.client.role})
              </Message.Header>
              <p className="tiny top margin">
                Please click "Sign" on each document below. After signing each
                document, be sure to click on the "Submit Form" button.
              </p>
              <Grid.Column computer={16}>
                <PartyDocSignList
                  docs={primaryClientDocs.docs}
                  column={docsForSigning.column}
                  direction={docsForSigning.direction}
                  signingAsParty={primaryClientDocs.client}
                />
              </Grid.Column>
            </Message>
          </Grid.Column>
        ) 
       }
       
       

        {/* Secondary Client Documents - Blue Section */
        }
        {secondaryClientDocs && secondaryClientDocs.docs?.length > 0 && (
          <Grid.Column computer={16} className="mini bottom margin">
            <Message
              fluid="true"
              color={secondaryClientDocs.client?.sharesEmailWithPrimary ? "blue" : "orange"}
              style={{ paddingTop: "26px", paddingBottom: "30px" }}
            >
              <Message.Header style={{ fontSize: "18px" }}>
                Documents to sign for {secondaryClientDocs.client.firstName}{" "}
                {secondaryClientDocs.client.lastName} ({secondaryClientDocs.client.role})
              </Message.Header>
              <p className="tiny top margin">
                You are signing on behalf of{" "}
                {secondaryClientDocs.client.firstName}{" "}
                {secondaryClientDocs.client.lastName}. There{" "}
                {secondaryClientDocs.docs.length === 1 ? "is" : "are"}{" "}
                {secondaryClientDocs.docs.length} document
                {secondaryClientDocs.docs.length === 1 ? "" : "s"} to sign.
              </p>
              <Grid.Column computer={16}>
                <PartyDocSignList
                  docs={secondaryClientDocs.docs}
                  column={docsForSigning.column}
                  direction={docsForSigning.direction}
                  signingAsParty={secondaryClientDocs.client}
                />
              </Grid.Column>
            </Message>
          </Grid.Column>
        )}

        {/* Tertiary Client Documents - Purple Section */}
        {tertiaryClientDocs && tertiaryClientDocs.docs.length > 0 && (
          <Grid.Column computer={16} className="mini bottom margin">
            <Message
              fluid="true"
              color={party.sharesEmailWithPrimary ? "purple" : "orange"}
              style={{ paddingTop: "26px", paddingBottom: "30px" }}
            >
              <Message.Header style={{ fontSize: "18px" }}>
                Documents to sign for {tertiaryClientDocs.client.firstName}{" "}
                {tertiaryClientDocs.client.lastName} ({tertiaryClientDocs.client.role})
              </Message.Header>
              <p className="tiny top margin">
                You are signing on behalf of{" "}
                {tertiaryClientDocs.client.firstName}{" "}
                {tertiaryClientDocs.client.lastName}. There{" "}
                {tertiaryClientDocs.docs.length === 1 ? "is" : "are"}{" "}
                {tertiaryClientDocs.docs.length} document
                {tertiaryClientDocs.docs.length === 1 ? "" : "s"} to sign.
              </p>
              <Grid.Column computer={16}>
                <PartyDocSignList
                  docs={tertiaryClientDocs.docs}
                  column={docsForSigning.column}
                  direction={docsForSigning.direction}
                  signingAsParty={tertiaryClientDocs.client}
                  // tableRowClassName="small-header-purple"
                />
              </Grid.Column>
            </Message>
          </Grid.Column>
        )}

        {/* Documents to sign for non-clients like Other or attorney - Orange Section */}
        {docsForSigning && docsForSigning.docs?.length > 0 && !primaryClientDocs && !secondaryClientDocs && !tertiaryClientDocs && (
        <Grid.Column computer={16} className="mini bottom margin">
            <Message
              fluid="true"
              color="orange"
              style={{ paddingTop: "26px", paddingBottom: "30px" }}
            >
              <Message.Header style={{ fontSize: "18px" }}>
                Documents to sign 
              </Message.Header>
              <p className="tiny top margin">
                Please click "Sign" on each document below. After signing each
                document, be sure to click on the "Submit Form" button.
              </p>
              <Grid.Column computer={16}>
                <PartyDocSignList
                  docs={docsForSigning.docs}
                  column={docsForSigning.column}
                  direction={docsForSigning.direction}
                  signingAsParty={docsForSigning.client}
                />
              </Grid.Column>
            </Message>
          </Grid.Column>  
       )
        }

        <Grid.Column computer={16}>
          <Message
            fluid="true"
            style={{ paddingTop: "26px", paddingBottom: "30px" }}
          >
            <Message.Header style={{ fontSize: "18px" }}>
              Shared Documents
            </Message.Header>
            <p className="tiny top margin">
              The following documents have been shared with you.
              {/* There is no action needed on them. */}
            </p>
            <Grid className="tiny bottom margin">
              <Grid.Column computer={8} mobile={16}>
                <Input
                  type="text"
                  fluid
                  placeholder="Search by document name or status"
                  value={searchTerms}
                  onChange={(e) => setSearchTerms(e.target.value)}
                ></Input>
              </Grid.Column>
              <Grid.Column width={8}></Grid.Column>
            </Grid>
            {docsViewing?.length > 0 ? (
              <Grid.Column computer={16}>
                <PartyDocViewList
                  docs={docsViewing}
                  column={docsForViewing.column}
                  direction={docsForViewing.direction}
                />
              </Grid.Column>
            ) : (
              <p>There are no shared documents</p>
            )}
          </Message>
        </Grid.Column>

        {(transaction?.agentRepresents === "Seller" &&
          party?.role !== "Buyer" &&
          party?.role !== "Buyer 2" &&
          party?.role !== "Buyer 3") ||
        (transaction?.agentRepresents === "Buyer" &&
          party?.role !== "Seller" &&
          party?.role !== "Seller 2" &&
          party?.role !== "Seller 3") ? (
          <>
            <Grid.Column
              computer={16}
              className="zero bottom padding large top margin"
            >
              {transaction?.agentProfile?.brokerageLogoRef && (
                <Image
                  src={transaction.agentProfile.brokerageLogoRef}
                  size="medium"
                />
              )}
            </Grid.Column>
            <Grid.Column computer={16} className="zero bottom padding">
              <div>
                <span className="bold">
                  {transaction?.agentName ||
                    transaction?.agentProfile?.firstName +
                      " " +
                      transaction?.agentProfile?.lastName}
                </span>
                <br />
                {transaction?.agentProfile?.brokerageName}
                <br />
                {transaction?.agentProfile?.email}
                <br />
                {transaction?.agentProfile?.phone}
              </div>
            </Grid.Column>
          </>
        ) : (
          <></>
        )}
      </Grid>
    </>
  );
}
